import { SurveyApi } from "@/apis/v2/SurveyApi";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = 'Surveys';
const RESPONSE_PAGE_SIZE = 20;

function handleError(error) {
    if (!error?.response) {
        customToast.error('서버와의 연결이 원활하지 않습니다.');
        return;
    }

    customToast.error(error.response?.data?.message);
}

// 내 설문 목록 (최근 수정순)
export function useMySurveysQuery({ userPageId }) {
    return useQuery({
        queryKey: [QUERY_KEY, 'list', userPageId],
        enabled: !!userPageId,
        queryFn: async () => {
            const response = await SurveyApi.getSurveys({ params: { userPageId } });

            return response?.data?.data?.items || [];
        },
    });
}

// 수정할 설문 (질문 포함). 열 때마다 새로 받는다
export function useSurveyQuery({ id }) {
    return useQuery({
        queryKey: [QUERY_KEY, 'one', id],
        enabled: !!id,
        staleTime: 0,
        gcTime: 0,
        queryFn: async () => {
            const response = await SurveyApi.getSurvey({ id });

            return response?.data?.data;
        },
    });
}

// 질문별 요약 (응답 탭을 열 때마다)
export function useSurveySummaryQuery({ id }) {
    return useQuery({
        queryKey: [QUERY_KEY, 'summary', id],
        enabled: !!id,
        staleTime: 0,
        queryFn: async () => {
            const response = await SurveyApi.getSummary({ id });

            return response?.data?.data;
        },
    });
}

// 개별 응답 (최신순, 더 보기)
export function useSurveyResponsesQuery({ id }) {
    return useInfiniteQuery({
        queryKey: [QUERY_KEY, 'responses', id],
        enabled: !!id,
        staleTime: 0,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const response = await SurveyApi.getResponses({ id, params: { offset: pageParam, limit: RESPONSE_PAGE_SIZE } });

            return response?.data?.data;
        },
        getNextPageParam: (lastPage) => {
            const nextOffset = lastPage.offset + lastPage.items.length;
            return nextOffset < lastPage.total ? nextOffset : undefined;
        },
    });
}

function useSurveyMutation(mutationFn) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await mutationFn(variables);

            return response?.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: handleError,
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useCreateSurveyMutation() {
    return useSurveyMutation(({ body }) => SurveyApi.createSurvey({ body }));
}

export function useUpdateSurveyMutation() {
    return useSurveyMutation(({ id, body }) => SurveyApi.updateSurvey({ id, body }));
}

export function useDeleteSurveyMutation() {
    return useSurveyMutation(({ id }) => SurveyApi.deleteSurvey({ id }));
}

export function useDeleteSurveyResponseMutation() {
    return useSurveyMutation(({ id, responseId }) => SurveyApi.deleteResponse({ id, responseId }));
}
